import { type ReactNode, useState } from 'react';

import { Box, type SxProps, TextField, type Theme } from '@mui/material';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { type LucideIcon } from 'lucide-react';

import { PhoneInput } from '@modules/_shared/components/PhoneInput';
import { isEmailValid } from '@modules/_shared/utils/email-validation';
import { formatPhoneNumberE164 } from '@modules/_shared/utils/phone';
import { SaveCancelButtons } from '@modules/users/user-overview/components/SaveCancelButtons';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

type ContactKind = 'email' | 'phone';

type ContactEditFormProps = {
  icon: LucideIcon;
  kind: ContactKind;
  initialValue?: string;
  trailing?: ReactNode;
  onSubmit: (value: string) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  sx?: SxProps<Theme>;
};

export const ContactEditForm = ({
  icon: Icon,
  kind,
  initialValue = '',
  trailing,
  onSubmit,
  onCancel,
  isSubmitting,
  sx,
}: ContactEditFormProps) => {
  const { t } = useLocale();
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  const validate = (next: string): string | null => {
    const trimmed = next.trim();
    if (trimmed.length === 0) {
      return t('This field is required');
    }
    if (kind === 'email') {
      return isEmailValid(trimmed) ? null : t('Invalid email');
    }
    const phone = parsePhoneNumberFromString(trimmed);
    return phone?.isValid() ? null : t('phoneNumberInvalid');
  };

  const handleSubmit = async () => {
    const validationError = validate(value);
    if (validationError) {
      setError(validationError);
      return;
    }
    const normalised =
      kind === 'phone' ? formatPhoneNumberE164(value) ?? value : value.trim();
    await onSubmit(normalised);
  };

  const handleChange = (next: string) => {
    setValue(next);
    if (error) {
      setError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      background: customPalette.white,
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-input': { py: '6px' },
  };

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
      ]}
    >
      <Box
        sx={{
          color: 'text.secondary',
          display: 'flex',
          flexShrink: 0,
          width: 16,
          mt: '7px',
        }}
      >
        <Icon size={16} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {kind === 'phone' ? (
          <PhoneInput
            value={value}
            onChange={handleChange}
            placeholder={t('phoneNumber')}
            size="small"
            variant="outlined"
            error={!!error}
            helperText={error ?? undefined}
            disabled={isSubmitting}
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { onKeyDown: handleKeyDown } }}
            sx={inputSx}
          />
        ) : (
          <TextField
            value={value}
            onChange={evt => handleChange(evt.target.value)}
            placeholder={t('Email')}
            type="email"
            autoComplete="email"
            size="small"
            variant="outlined"
            error={!!error}
            helperText={error ?? undefined}
            disabled={isSubmitting}
            autoFocus
            fullWidth
            onKeyDown={handleKeyDown}
            sx={inputSx}
          />
        )}
      </Box>

      {trailing}

      <SaveCancelButtons
        onSave={handleSubmit}
        onCancel={onCancel}
        disabled={isSubmitting}
      />
    </Box>
  );
};
